import { useCallback, useEffect, useRef, useState } from 'react'
import { useMsal } from '@azure/msal-react'
import { getHistory } from '../api/history'
import type { HistoryResponse } from '../api/historyTypes'
import { appendPoints, HistoryPoints, HistorySeries, toPoints, toSeries } from '../lib/history'

export interface HistoryRange {
  from: number            // epoch seconds
  to: number | 'now'      // 'now' = keep following live data
  key: string             // changes whenever the range should be refetched
}

export type HistoryState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ready'; resp: HistoryResponse; points: HistoryPoints; series: HistorySeries; refreshedAt: number }

/** Live ranges: 30 s data is extended every minute; aggregated views are refetched every 5. */
const TAIL_MS = 60_000
const AGG_REFRESH_MS = 300_000

function message(err: unknown): string {
  const e = err as { status?: number; message?: string }
  if (e?.status === 403) return "History isn't available for this system yet."
  return "History couldn't be loaded. Check your connection and try again."
}

export function useHistory(systemId: number, range: HistoryRange) {
  const { instance } = useMsal()
  const [state, setState] = useState<HistoryState>({ status: 'loading' })
  const stateRef = useRef(state)
  stateRef.current = state
  const reqId = useRef(0)

  const load = useCallback(async () => {
    const id = ++reqId.current
    // Keep showing the previous chart while the next range loads.
    if (stateRef.current.status !== 'ready') setState({ status: 'loading' })
    try {
      const now = Math.floor(Date.now() / 1000)
      const resp = await getHistory(instance, {
        systemId, from: range.from, to: range.to === 'now' ? now : range.to,
      })
      if (id !== reqId.current) return
      const points = toPoints(resp)
      setState({ status: 'ready', resp, points, series: toSeries(points), refreshedAt: Date.now() })
    } catch (err) {
      if (id !== reqId.current) return
      setState({ status: 'error', message: message(err) })
    }
  }, [instance, systemId, range.key]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load() }, [load])

  // Follow live data when the range ends "now".
  useEffect(() => {
    if (range.to !== 'now') return
    const timer = window.setInterval(async () => {
      const cur = stateRef.current
      if (cur.status !== 'ready' || document.hidden) return
      if (cur.resp.resolution !== '30s') {
        if (Date.now() - cur.refreshedAt >= AGG_REFRESH_MS) load()
        return
      }
      const last = cur.points.t.length ? cur.points.t[cur.points.t.length - 1] : range.from
      const id = reqId.current
      try {
        const tail = await getHistory(instance, { systemId, since: last })
        if (id !== reqId.current || stateRef.current !== cur) return
        const points = appendPoints(cur.points, tail)
        if (points === cur.points) return
        setState({ ...cur, points, series: toSeries(points), refreshedAt: Date.now() })
      } catch {
        // A missed tail is picked up by the next one; keep the chart as it is.
      }
    }, TAIL_MS)
    return () => window.clearInterval(timer)
  }, [instance, systemId, range.key, range.to, range.from, load])

  return { state, reload: load }
}
